import { IAddProject, IImageInfo, ILabelCount, IResponseProjectData } from "@types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  applyAILabel,
  autoEditImageLabel,
  editImageLabel,
  editProjectConfig,
  getImageLabelsForValid,
  getImageSort,
  getLatestTrainHistory,
  getProject,
  getProjectLastLabelSeq,
  updateDatasetFile,
} from "../../services/projectService";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import {
  dataLoadingState,
  IProjectCountInfo,
  projectConfigState,
  projectCountInfoState,
  projectImagesInfoState,
  projectInfoState,
  selectedImageState,
  multiSelectedSeqsState,
  tableColumnState,
  trainStatusInfoState,
} from "../../recoil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Button,
  TableSortLabel,
} from "@mui/material";
import { t } from "i18next";
import { useTrainHandler } from "../../hooks/useTrainHandler";
import { extractImgPaths } from "../../utils/datasetFileUtil";
import {
  imageViewModeState,
  myAIModuleState,
  trainingStateAtom,
  trainUuidState,
  validatingStateAtom,
} from "../../recoil/upgrade";
import { toast } from "react-toastify";
import { useTrainingStream } from "../../hooks/useTrainingStream";
import { Crosshair, Import, ListFilter, Settings } from "lucide-react";
import { startTrain } from "../../services/visionService";
// import { startTrain } from "common-lib";
import {
  clearImgData,
  updateAIModules,
  applyAutoTrainCondition,
  extractFileName,
  getDatasetInfo,
  getFile,
  StopIcon,
  truncateString,
} from "common-lib";
import { stopProjectTrain } from "../../services/trainService";
import { swBridge } from "../../utils/sw-register";
import { FixedSizeList as List } from "react-window";
import { VirtualizedSelectableList } from "common_ui_lib";
import { useSWCheck } from "../../hooks/useSWcheck";
import SWProgressWarning from "../SWProgressWarning";
import { AITableCell } from "./AITableCell";
import TrainingProgressChip from "./ProjectTableToolbar/TrainProgressChip";
import TrainPopover from "./ProjectTableToolbar/TrainPopover";
import TrainSettingPanel from "./ProjectTableToolbar/TrainingSettingPanel";
import TableToolsPanel, {
  exportCsv,
  getMismatchPairs,
  matchesMismatch,
  MismatchFilter,
  nextNearThreshold,
  ThresholdJump,
} from "./ProjectTableToolbar/TableToolsPanel";
import { useLabeling } from "../../context/LabelingContext";
import { useProjectImagePolling } from "../../hooks/useProjectImagePolling";
import { TUTORIALS } from "../../tutorial";
import { ClsTrainWorkSteps } from "../../tutorial/steps/cls-train-work";
import { AnaLabelingSteps } from "../../tutorial/steps/ana-labeling";
import { useTutorial } from "../../context/TutorialContext";
import { useConfirm } from "../confirm/ConfirmProvider";

// 헤더(table)와 바디(flex 행)가 같은 비율을 쓰도록 하는 열 폭 가중치
const COLUMN_WEIGHT: Record<string, number> = { no: 1, image: 3, label: 2, aiLabel: 2, score: 2 };
const getColumnWeight = (field: string) => COLUMN_WEIGHT[field] ?? 2;

export interface ImageInfoItem {
  AILabel: string;
  ai_update_at: string;
  id: number;
  label: string;
  path: string;
  recentLabel: string;
  score: number;
  seq: number;
}

interface ImageTableProps {
  dataSetId: number;
}

export interface FilePathList {
  unlabeled: string[];
  [label: string]: string[];
}

const site = "FUREX";

const getDefaultMinTrainCount = (projectCountInfo: IProjectCountInfo | null | undefined, label: string): number => {
  const count = projectCountInfo?.label_count?.[label];
  return typeof count === "number" && Number.isFinite(count) && count > 0 ? count : 10;
};

type SortState = { key: "label" | "aiLabel" | "score"; dir: "asc" | "desc" } | null;
const SORTABLE_FIELDS = ["label", "aiLabel", "score"];

// 기본 정렬: 라벨=AI일치(score↑) → 불일치(score↓) → 미라벨(score↓)
const defaultSort = (items: IImageInfo[]) => {
  const getGroup = (item: IImageInfo) => (!item.label ? 2 : item.label === item.AILabel ? 0 : 1);
  return [...items].sort((a, b) => {
    const ga = getGroup(a);
    const gb = getGroup(b);
    if (ga !== gb) return ga - gb;
    return ga === 0 ? a.score - b.score : b.score - a.score;
  });
};

const sortImages = (items: IImageInfo[], sort: SortState) => {
  if (!sort) return defaultSort(items);
  const d = sort.dir === "asc" ? 1 : -1;
  if (sort.key === "score") return [...items].sort((a, b) => (a.score - b.score) * d);
  const field = sort.key === "aiLabel" ? "AILabel" : "label";
  // 같은 라벨 안에서는 score 오름차순 (경계 데이터 먼저)
  return [...items].sort(
    (a, b) => String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * d || a.score - b.score,
  );
};

export default function ImageTable({ dataSetId }: ImageTableProps) {
  const { id } = useParams();
  const { label } = useParams();
  const navigate = useNavigate();
  const size = 100;
  const rowRef = useRef<Record<string, HTMLTableRowElement | null>>({});
  const listRef = useRef<List | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState<string | null>(null);
  const [projectImagesInfo, setProjectImagesInfo] = useRecoilState(projectImagesInfoState);
  const [selectedRow, setSelectedRow] = useRecoilState(selectedImageState);
  const [isTimeOut, setIsTimeOut] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useRecoilState(dataLoadingState);

  const [trainingState, setTrainingState] = useRecoilState(trainingStateAtom);
  const [validatingState, setValidatingState] = useRecoilState(validatingStateAtom);
  const [trainedImageCount, setTrainedImageCount] = useState(0);
  const [autoValid, setAutoValid] = useState<boolean>(false);
  const [sendCount, setSendCount] = useState<number>(0);

  const projectData = useRecoilValue(projectInfoState);
  const [projectCountInfo, setProjectCountInfo] = useRecoilState(projectCountInfoState);
  const [myAIModule, setMyAIModule] = useRecoilState(myAIModuleState);
  const [trainUuid, setTrainUuid] = useRecoilState(trainUuidState);
  const resetImageViewMode = useResetRecoilState(imageViewModeState);
  const [inspResult, setInspResult] = useState<any>([]);
  const [progress, setProgress] = useState<{
    count: number;
    totalCount: number;
  }>({ count: 0, totalCount: 0 });
  const prevSelectedRowIdRef = useRef<number | null>(null);

  const [projectConfig, setProjectConfig] = useRecoilState(projectConfigState);
  const [openPlayOption, setOpenPlayOption] = useState<boolean>(false);
  const playOptionBoxRef = useRef<HTMLDivElement>(null);

  const [openSetting, setOpenSetting] = useState(false);
  const [autoLabeling, setAutoLabeling] = useState(false);
  const [thresholdCustom, setThresholdCustom] = useState<number>(0.5);
  const [targetLabel, setTargetLabel] = useState<string>("OK");
  const [minTrainCount, setMinTrainCount] = useState<number>(() => getDefaultMinTrainCount(projectCountInfo, "OK"));
  const [autoLabelingCondition, setAutoLabelingCondition] = useState<string>("");
  const conditionList = ["+30", "*3"];
  const [fields, setFields] = useRecoilState(tableColumnState);

  const [trainPopOpen, setTrainPopOpen] = useState(false);
  const [trainCount, setTrainCount] = useState<string>(() => {
    if (!projectData?.counts?.label_count || !projectConfig?.trainTargetLabel) return "";
    return (projectData.counts.label_count[projectConfig.trainTargetLabel] ?? "").toString();
  });
  const trainPopRef = useRef<HTMLDivElement>(null);
  const labeledCount = useMemo(() => projectImagesInfo.filter((r) => r.label).length, [projectImagesInfo]);

  const confirm = useConfirm();
  const swStatus = useSWCheck();
  const swUnavailable = swStatus === "not_supported" || swStatus === "not_registered" || swStatus === "no_controller";
  const location = useLocation();
  const totalFileCount = location.state?.totalFileCount ?? 0;

  const { run, tutorialKey, nextSubStep, currentStep, stepIndex } = useTutorial();

  // 정렬은 recoil 배열 자체에 적용 → 키보드 이전/다음 이동도 화면 순서를 따름
  const [sort, setSort] = useState<SortState>(null);
  const sortRef = useRef<SortState>(null); // polling 중 fetchData 클로저용

  const handleSort = (key: NonNullable<SortState>["key"]) => {
    // asc → desc → 기본 정렬
    const next: SortState = sort?.key !== key ? { key, dir: "asc" } : sort.dir === "asc" ? { key, dir: "desc" } : null;
    sortRef.current = next;
    setSort(next);
    setProjectImagesInfo((prev) => sortImages(prev, next));
  };

  const threshold: number | undefined = myAIModule?.options?.labelscore?.para?.threshold_list?.[0]?.toFixed(4);

  // 불일치 필터: 켜는 시점의 seq 스냅샷으로 고정 → 라벨 수정해도 행이 목록에서 사라지지 않음
  const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
  const [mismatchFilter, setMismatchFilter] = useState<MismatchFilter>(null);
  const [filterSeqs, setFilterSeqs] = useState<Set<number> | null>(null);

  // 다중 선택 (Shift+클릭)
  const [multiSeqs, setMultiSeqs] = useRecoilState(multiSelectedSeqsState);
  const selectedSeqs = useMemo(() => new Set(multiSeqs), [multiSeqs]);
  const setSelectedSeqs = (seqs: Set<number>) => setMultiSeqs(Array.from(seqs));
  const anchorSeqRef = useRef<number | null>(null);
  const clickModRef = useRef({ shift: false, ctrl: false });

  const mismatchPairs = useMemo(() => getMismatchPairs(projectImagesInfo), [projectImagesInfo]);

  const handleFilterChange = (f: MismatchFilter) => {
    setMismatchFilter(f);
    setSelectedSeqs(new Set());
    if (!f) return setFilterSeqs(null);
    setFilterSeqs(new Set(projectImagesInfo.filter((r) => matchesMismatch(f, r)).map((r) => r.seq!)));
  };

  const viewItems = useMemo(
    () => (filterSeqs ? projectImagesInfo.filter((r) => filterSeqs.has(r.seq!)) : projectImagesInfo),
    [projectImagesInfo, filterSeqs],
  );

  // threshold와 score가 가장 가까운 행으로 이동
  const jumpRef = useRef<ThresholdJump>(null);
  const handleJumpToThreshold = () => {
    if (threshold === undefined || !viewItems.length) return;
    const { index, jump } = nextNearThreshold(viewItems, Number(threshold), jumpRef.current, selectedRow?.path);
    jumpRef.current = jump;
    setSelectedRow(viewItems[index]);
    listRef.current?.scrollToItem(index, "center");
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMultiSeqs([]);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      setMultiSeqs([]); // 다른 프로젝트로 선택이 넘어가지 않도록
    };
  }, []);

  const handleExportCsv = () =>
    exportCsv(viewItems, `project_${id}${label ? `_${label}` : ""}_${new Date().toISOString().slice(0, 10)}.csv`);

  useEffect(() => {
    console.log("count✅", projectData?.counts?.label_count);
  }, [projectData]);

  useEffect(() => {
    if (projectConfig) {
      setAutoLabeling(projectConfig.autoLabeling);
      setTargetLabel(projectConfig.trainTargetLabel! || "OK");
      setMinTrainCount(projectConfig.trainConditionCount);
      setAutoLabelingCondition(projectConfig.autoLabelingCondition);
    }
  }, [projectConfig]);

  useEffect(() => {
    if (!projectData?.counts?.label_count || !projectConfig?.trainTargetLabel) return;
    const count = projectData.counts.label_count[projectConfig.trainTargetLabel];
    if (count != null) setTrainCount(count.toString());
  }, [projectData, projectConfig?.trainTargetLabel]);

  useEffect(() => {
    if (label) {
      setTrainUuid(`${id}_${label}`);
    }
  }, [label]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (playOptionBoxRef.current && !playOptionBoxRef.current.contains(event.target as Node)) {
        setOpenPlayOption(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isSaved = useMemo(() => {
    if (!projectConfig) return false;

    return (
      autoLabeling === projectConfig.autoLabeling &&
      minTrainCount === projectConfig.trainConditionCount &&
      targetLabel === projectConfig.trainTargetLabel &&
      autoLabelingCondition === projectConfig.autoLabelingCondition
    );
  }, [autoLabeling, minTrainCount, targetLabel, autoLabelingCondition, projectConfig]);

  const { fetchInspResult, inspStatus } = useTrainHandler({
    id,
    step: "분류",
    label: "",
    projectDataStep: "분류",
    t,
    // isTraining,
    // setIsTraining,
    isTrainable: true,
  });

  useEffect(() => {
    setTrainingState((prev) => ({
      ...prev,
      [Number(id)]: inspStatus?.state === "Training" ? true : false,
    }));

    setValidatingState((prev) => ({
      ...prev,
      [Number(id)]: inspStatus?.state === "Validating" ? true : false,
    }));
  }, [inspStatus?.state]);

  const fetchFileListByLabel = async () => {
    const dsInfo = await getDatasetInfo(dataSetId);
    const dsFileInfo = await getFile(dsInfo.data.path);
    const pathList = extractImgPaths(dsFileInfo.data) as FilePathList;
    return { dsFilePath: dsInfo.data.path, pathList: pathList };
  };

  const fetchData = async () => {
    if (!isPolling) setIsLoading(true);
    const response = await getProjectLastLabelSeq(Number(id));
    let data = [];

    if (label) {
      data = await getProject(Number(id), undefined, undefined, label);
    } else {
      data = await getProject(Number(id));
    }
    const sorted = sortImages(data.project.images as IImageInfo[], sortRef.current);

    setProjectImagesInfo(sorted);
    setProjectCountInfo({
      total_count: sorted.length,
      label_count: sorted.reduce((acc, img) => {
        if (img.label) {
          acc[img.label as string] = (acc[img.label as string] || 0) + 1;
        }
        acc.total = (acc.total || 0) + (img.label ? 1 : 0);
        return acc;
      }, {} as ILabelCount),
    });
    setIsLoading(false);
    setIsLoadingData(true);
  };

  const { isPolling, start } = useProjectImagePolling(fetchData, totalFileCount, projectImagesInfo.length);

  useEffect(() => {
    fetchData().then(() => {
      if (totalFileCount > 0) start();
    });
  }, []);

  useEffect(() => {
    if (projectData?.step === "분류") {
      const steps = TUTORIALS[tutorialKey];
      const imageLabelingIndex = steps.indexOf(ClsTrainWorkSteps[6]);

      if (!run || stepIndex !== imageLabelingIndex) return;
      console.log("imageLabelingIndex", imageLabelingIndex, projectCountInfo, stepIndex);
      if (projectCountInfo.label_count["OK"] > 9) {
        console.log("라벨링 완료");
        setTimeout(() => nextSubStep(), 100);
        return;
      }
    } else if (projectData?.step === "분석") {
      const steps = TUTORIALS[tutorialKey];
      const imageLabelingIndex = steps.indexOf(AnaLabelingSteps[6]);

      if (!run || stepIndex !== imageLabelingIndex) return;
      console.log("imageLabelingIndex", imageLabelingIndex, projectCountInfo, stepIndex);
      if (projectCountInfo.label_count["A"] > 9) {
        console.log("라벨링 완료");
        setTimeout(() => nextSubStep(), 100);
        return;
      }
    }
  }, [projectCountInfo, run, stepIndex]);

  const hasInitialScrolled = useRef(false);

  useEffect(() => {
    if (!projectImagesInfo.length || !listRef.current) return;
    if (hasInitialScrolled.current) return;

    const firstEmptyIndex = projectImagesInfo.findIndex((item) => !item.label);
    const targetIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : 0;

    setSelectedRow(projectImagesInfo[targetIndex]);
    listRef.current.scrollToItem(targetIndex, "center");

    hasInitialScrolled.current = true;
  }, [projectImagesInfo]);

  useEffect(() => {
    if (!selectedRow || !listRef.current) return;

    const index = viewItems.findIndex((image) => image.seq === selectedRow.seq);
    if (index === -1) return;

    listRef.current.scrollToItem(index, "center");
  }, [selectedRow]);

  useEffect(() => {
    if (projectData) {
      setSendCount(projectData?.counts.label_count[targetLabel]);
    }
  }, [projectData]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (trainPopRef.current && !trainPopRef.current.contains(e.target as Node)) {
        setTrainPopOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTrainWithCount = () => {
    const target = projectData?.step === "분석" ? label : targetLabel;

    const count = Number(trainCount) || projectData?.counts.label_count[target ?? ""];

    if (!count || count < 0) return;

    handleTrain(count);
    setTrainPopOpen(false);
    setTrainCount("");
  };

  useEffect(() => {
    fetchData();
    fetchInspResult();

    const timer = setTimeout(() => {
      setIsTimeOut(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [label]);

  useEffect(() => {
    if (!selectedRow) return;

    const prevId = prevSelectedRowIdRef.current;
    const currentId = selectedRow.id;

    const rowEl = rowRef.current[String(currentId)];
    const container = document.getElementById("table-container") as HTMLElement | null;

    if (!container || !rowEl || !currentId) return;

    const isMovingUp = prevId !== null && currentId < prevId;

    requestAnimationFrame(() => {
      const rowTop = rowEl.offsetTop;
      const rowBottom = rowTop + rowEl.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      const isVisible = rowTop >= containerScrollTop && rowBottom <= containerScrollTop + containerHeight;

      if (isMovingUp) {
        if (!isVisible || rowTop < containerScrollTop + 100) {
          container.scrollTo({
            top: rowTop - 100,
            behavior: "smooth",
          });
        }
      } else {
        container.scrollTo({
          top: rowTop - 700,
          behavior: "smooth",
        });
      }
    });

    prevSelectedRowIdRef.current = currentId;
  }, [selectedRow]);

  // 학습 상태 확인
  useTrainingStream({
    enabled: trainingState[Number(id)],
    mode: "train",
    trainUuid,
    projectData,
    trainedImageCount,
    sendCount,
    myAIModule,
    site,
    id,
    label: targetLabel,
    epoch: 1,
    setMyAIModule: setMyAIModule,
    onProgress: (data) => {
      setProgress(data);
    },
    onComplete: async () => {
      let fetchData;
      for (let i = 0; i < 3; i++) {
        fetchData = await getLatestTrainHistory(Number(id));
        if (fetchData.trainHistory.status === "Finished") break;
        await new Promise((res) => setTimeout(res, 500));
      }

      if (fetchData?.trainHistory.status !== "Finished") {
        console.warn("trainHistory Finished 아직 아님");
      }

      setTrainingState((prev) => ({ ...prev, [Number(id)]: false }));

      const newTrainConditionCount = applyAutoTrainCondition(
        projectConfig.trainConditionCount,
        projectConfig.autoLabelingCondition,
      );
    },
    onError: (err) => {
      setTrainingState((prev) => ({
        ...prev,
        [Number(id)]: false,
      }));
      console.error("스트림 오류:", err);
    },
  });

  // 검사 상태 확인
  useTrainingStream({
    enabled: validatingState[Number(id)],
    mode: "valid",
    trainUuid,
    projectData,
    trainedImageCount,
    sendCount: projectData?.step !== "분석" ? (projectData?.counts.label_count[targetLabel] ?? 0) : sendCount,
    myAIModule,
    site,
    id,
    label,
    setMyAIModule: setMyAIModule,
    onProgress: ({ extracted, count, totalCount, results }) => {
      if (extracted) setInspResult(extracted); // valid_starting은 extracted 없이 progress만 갱신
      setProgress({ count, totalCount });
    },
    onComplete: async () => {
      await new Promise((res) => setTimeout(res, 30));

      if (projectConfig.autoLabeling) {
        const currentOkCount = projectCountInfo.label_count["OK"] ?? 0;
        const autoLabelingConditionValue = Number(projectConfig.autoLabelingCondition.slice(1).trim());
        const newTrainConditionCount = currentOkCount + autoLabelingConditionValue;

        await editProjectConfig(Number(id), {
          ...projectConfig,
          trainConditionCount: newTrainConditionCount,
        });
        setProjectConfig({
          ...projectConfig,
          trainConditionCount: newTrainConditionCount,
        });
      }

      setValidatingState((prev) => ({
        ...prev,
        [Number(id)]: false,
      }));
      toast.success(t("InspectionCompleteMsg"));
      if (await confirm({ message: t("ReSortImagesConfirmMsg"), autoConfirmSec: 10 })) {
        await getImageSort(Number(id));
        fetchData();
      }
    },
    onError: (err) => {
      setValidatingState((prev) => ({
        ...prev,
        [Number(id)]: false,
      }));
      console.error("스트림 오류:", err);
    },
  });

  // Shift+클릭: anchor ~ 클릭 행 범위 선택, Ctrl+클릭: 단일 행 추가/해제
  // (list 라이브러리가 click event를 안 넘겨줘서 셀 mousedown에서 modifier 기록)
  const handleRowClick = (row: IImageInfo) => {
    const { shift, ctrl } = clickModRef.current;
    if (ctrl) {
      const next = new Set(selectedSeqs.size ? selectedSeqs : selectedRow?.seq != null ? [selectedRow.seq] : []);
      next.has(row.seq!) ? next.delete(row.seq!) : next.add(row.seq!);
      setSelectedSeqs(next);
      anchorSeqRef.current = row.seq ?? null;
    } else if (shift && anchorSeqRef.current != null) {
      const a = viewItems.findIndex((r) => r.seq === anchorSeqRef.current);
      const b = viewItems.findIndex((r) => r.seq === row.seq);
      if (a !== -1 && b !== -1) {
        setSelectedSeqs(new Set(viewItems.slice(Math.min(a, b), Math.max(a, b) + 1).map((r) => r.seq!)));
      }
    } else {
      anchorSeqRef.current = row.seq ?? null;
      setSelectedSeqs(new Set());
    }
    setSelectedRow(row);
  };

  const getDividerIndices = () => {
    const threshold = myAIModule?.options?.labelscore?.para?.threshold_list?.[0];
    if (threshold === undefined) return new Set<number>();

    const dividers = new Set<number>();

    let groupStart = 0;

    const group0End = projectImagesInfo.findIndex((item) => item.label && item.label !== item.AILabel);
    for (let i = groupStart; i < (group0End === -1 ? projectImagesInfo.length : group0End); i++) {
      if (projectImagesInfo[i].score >= threshold) {
        dividers.add(i);
        break;
      }
    }

    groupStart = group0End === -1 ? projectImagesInfo.length : group0End;
    const group1End = projectImagesInfo.findIndex((item) => !item.label);
    for (let i = groupStart; i < (group1End === -1 ? projectImagesInfo.length : group1End); i++) {
      if (projectImagesInfo[i].score < threshold) {
        dividers.add(i);
        break;
      }
    }

    groupStart = group1End === -1 ? projectImagesInfo.length : group1End;
    for (let i = groupStart; i < projectImagesInfo.length; i++) {
      if (projectImagesInfo[i].score < threshold) {
        dividers.add(i);
        break;
      }
    }

    return dividers;
  };

  // 자동 라벨링
  const isAutoLabelingTriggeredRef = useRef(false);

  useEffect(() => {
    if (
      !projectConfig?.autoLabeling ||
      !projectCountInfo?.label_count ||
      trainingState[Number(id)] ||
      validatingState[Number(id)] ||
      myAIModule?.serverAddress == null ||
      isAutoLabelingTriggeredRef.current
    )
      return;

    const targetLabel = projectConfig.trainTargetLabel;
    const targetCount = projectCountInfo.label_count[targetLabel] ?? 0;

    if (autoLabeling && targetCount >= projectConfig.trainConditionCount && targetLabel !== "") {
      isAutoLabelingTriggeredRef.current = true;
      toast(t("AutoLabelingStartMsg"));
      handleTrain(Number(trainCount));
    }
  }, [projectConfig, projectCountInfo, targetLabel, trainingState, validatingState, myAIModule, autoLabeling]);

  useEffect(() => {
    if (trainingState[Number(id)]) {
      isAutoLabelingTriggeredRef.current = false;
    }
  }, [trainingState, id]);

  const handleTrain = async (count: number) => {
    if (!myAIModule && trainingState[Number(id)]) return;
    console.log("myAIModule", myAIModule);
    if (myAIModule && myAIModule!.serverAddress && myAIModule?.desc.preview) {
      try {
        resetImageViewMode();

        setLoadingStatus(t("DatasetUpdatingMsg"));
        await updateDatasetFile(Number(id));

        setLoadingStatus(t("TrainPreparingMsg"));
        await clearImgData(myAIModule!.serverAddress, trainUuid);

        const response = await fetchFileListByLabel();
        const pathList = response.pathList;

        if (pathList && myAIModule) {
          let labelPaths: string[] = [];
          if (projectData?.step !== "분석") {
            labelPaths = pathList[projectConfig.trainTargetLabel!];
          } else {
            labelPaths = pathList[label!];
          }
          const finalPaths =
            !count || count >= labelPaths.length
              ? labelPaths
              : [...labelPaths].sort(() => Math.random() - 0.5).slice(0, count);

          setSendCount(finalPaths.length);

          const data = {
            train_id: trainUuid,
            site: site,
            type: "train",
            total: projectData?.counts.total_count,
            trained_count: projectData?.counts.label_count[projectConfig.trainTargetLabel!],
          };

          setLoadingStatus(t("AIModuleUpdatingMsg"));
          const updateResult = await updateAIModules(myAIModule.serverAddress, data, myAIModule);

          setMyAIModule({
            ...myAIModule,
            desc: {
              ...myAIModule.desc,
              infer: false,
              train: true,
              preview: false,
            },
          });

          if (!updateResult) {
            throw new Error(t("AIModuleUpdateFailedMsg"));
          }

          setLoadingStatus(t("TrainPreparingMsg"));
          await startTrain({
            id,
            uuid: trainUuid,
            dataset_path: response.dsFilePath,
            auto_valid: autoValid,
            ip: myAIModule.serverAddress,
            epoch: null,
            saveOption: null,
            parentModel: null,
            trainCount: count,
            isMulti: projectData?.step === "분석" ? true : false,
          });

          setTrainingState((prev) => ({
            ...prev,
            [Number(id)]: true,
          }));

          toast.success(t("TrainStartedMsg"));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : t("TrainStartErrorMsg");
        toast.error(message);
        isAutoLabelingTriggeredRef.current = false; // 실패 시 재시도 가능하도록 해제
      } finally {
        setLoadingStatus(null);
      }
    }
  };

  const handleStopTrain = async () => {
    if (!myAIModule || !trainUuid) return;
    const ok = await confirm({
      title: t("StopConfirmTitle"),
      message: t("StopConfirmMsg"),
      destructive: true,
      confirmText: t("Confirm"),
    });
    if (ok) {
      try {
        await stopProjectTrain({ uuid: trainUuid, ip: myAIModule.serverAddress });
        swBridge.stopPolling(trainUuid);
        swBridge.clearCache(trainUuid);
        toast.success(t("TrainStoppedMsg"));
        setMyAIModule((prev) =>
          prev
            ? {
                ...prev,
                desc: {
                  ...prev.desc,
                  train: false,
                  preview: true,
                },
              }
            : prev,
        );
        setTrainingState((prev) => ({
          ...prev,
          [Number(id)]: false,
        }));
        setValidatingState((prev) => ({
          ...prev,
          [Number(id)]: false,
        }));
      } catch (error) {
        console.error("학습 중지 실패:", error);
        toast.error(t("StopTrainErrorMsg"));
      }
    }
  };

  const stateSetters: Record<string, React.Dispatch<React.SetStateAction<any>>> = {
    thresholdCustom: setThresholdCustom,
    minTrainCount: setMinTrainCount,
    targetLabel: setTargetLabel,
    autoLabelingCondition: setAutoLabelingCondition,
  };

  // 공통 onChange 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const setter = stateSetters[name];
    if (setter) {
      setter(value);
    }
  };

  const handleSaveConfig = async () => {
    const shouldConfirm = projectCountInfo.label_count[targetLabel] >= minTrainCount && autoLabeling;

    const newConfig = {
      ...projectConfig,
      autoLabeling: autoLabeling,
      trainConditionCount: minTrainCount,
      trainTargetLabel: targetLabel,
      autoLabelingCondition: autoLabelingCondition,
    };

    if (shouldConfirm) {
      const proceed = window.confirm(t("AutoLabelingConditionMetConfirmMsg"));
      if (!proceed) return;
    }

    try {
      await editProjectConfig(Number(id), newConfig);

      setProjectConfig(newConfig);

      toast.success(t("UpdateSuccessMsg"));
    } catch (error) {
      console.error(error);
    }
  };

  const renderCellContent = (field: string, row: IImageInfo, index: number) => {
    switch (field) {
      case "no":
        return index + 1;
      case "image":
        return (
          <Tooltip title={row.path}>
            <span>{truncateString(extractFileName(row.path), 10)}</span>
          </Tooltip>
        );
      case "label":
        return row.label;
      case "aiLabel":
        return (
          <AITableCell inspStatus={inspStatus} inspResult={inspResult} row={row} isVisible={true} column="aiLabel" />
        );
      case "score":
        return (
          <AITableCell inspStatus={inspStatus} inspResult={inspResult} row={row} isVisible={true} column="score" />
        );
      case "general":
        let format: any = {};
        try {
          if (row.general && typeof row.general === "string") {
            format = JSON.parse(row.general);
          } else if (typeof row.general === "object" && row.general !== null) {
            format = row.general;
          } else {
            format = {};
          }
        } catch (e) {
          console.warn("Invalid JSON in row.general:", row.general);
          format = {};
        }

        return format.high_pixel ?? "";
      default:
        return "";
    }
  };

  const handleOpenTrainPopover = () => {
    setTrainPopOpen(true);
    setTrainCount(String(labeledCount)); // 기본값: label(사람 라벨)이 채워진 이미지 수
  };

  const handleApplyAILabels = async () => {
    if (!window.confirm(t("OverwriteWithAIResultConfirmMsg"))) return;

    if (!label) return;
    try {
      const response = await applyAILabel(Number(id), label);
    } catch (error) {
      console.error("AI 라벨 적용 실패:", error);
      alert(t("ApplyAILabelErrorMsg"));
    }
  };

  const onAutoLabelingChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setAutoLabeling(checked);
  };

  const AUTO_LABELING_PANEL_SUBSTEP = TUTORIALS[tutorialKey].indexOf(ClsTrainWorkSteps[4]);
  useEffect(() => {
    if (run && stepIndex === AUTO_LABELING_PANEL_SUBSTEP) {
      setOpenSetting(false);
    }
  }, [stepIndex, run]);

  const handleAnnotation = async () => {
    const dsInfo = await getDatasetInfo(dataSetId);

    await startTrain({
      id,
      uuid: trainUuid,
      dataset_path: dsInfo.data.path,
      auto_valid: autoValid,
      ip: myAIModule?.serverAddress,
      epoch: null,
      saveOption: null,
      parentModel: null,
      trainCount: 15,
      isMulti: projectData?.step === "분석" ? true : false,
    });
    await clearImgData(myAIModule!.serverAddress, trainUuid);
  };
  return (
    <>
      <Box
        id="ai-progress"
        height="40px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="var(--stroke)"
        px={2}
        gap={1}
        sx={{
          minWidth: 0,
          backgroundColor: "var(--table-meta-bg)",
          borderBottom: "1px solid var(--table-meta-border)",
          color: "var(--table-meta-text)",
        }}
      >
        <Tooltip
          title={
            <span style={{ whiteSpace: "pre-line" }}>
              {Object.entries(projectCountInfo.label_count || {})
                .filter(([label]) => label !== "total")
                .map(([label, count]) => `${label}: ${count}${t("SheetCountUnit")}`)
                .concat(`${t("Total")}: ${projectCountInfo.label_count.total}${t("SheetCountUnit")}`)
                .join("\n")}
            </span>
          }
        >
          <span
            style={{
              color: "var(--table-meta-text)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {projectCountInfo.label_count.total}/{projectCountInfo.total_count}
            {t("SheetCountUnit")}
          </span>
        </Tooltip>

        <TableToolsPanel
          anchorEl={toolsAnchor}
          onClose={() => setToolsAnchor(null)}
          mismatchPairs={mismatchPairs}
          filter={mismatchFilter}
          onFilterChange={handleFilterChange}
          selectedCount={selectedSeqs.size}
          onClearSelection={() => setSelectedSeqs(new Set())}
          visibleCount={viewItems.length}
          onExportCsv={handleExportCsv}
        />
        {trainingState[Number(id)] ? (
          swUnavailable ? (
            <SWProgressWarning />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0, flexShrink: 1 }}>
              <TrainingProgressChip type="학습" count={progress.count} totalCount={progress.totalCount} />
              <Tooltip title={t("StopTrain")}>
                <IconButton size="small" onClick={handleStopTrain} sx={{ flexShrink: 0 }}>
                  <StopIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        ) : validatingState[Number(id)] ? (
          swUnavailable ? (
            <SWProgressWarning />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0, flexShrink: 1 }}>
              <TrainingProgressChip type="검사" count={progress.count} totalCount={progress.totalCount} />
              <Tooltip title={t("StopInspection")}>
                <IconButton size="small" onClick={handleStopTrain} sx={{ flexShrink: 0 }}>
                  <StopIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        ) : (
          <>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Box sx={{ display: "flex", gap: "3px" }} alignItems="center">
                <TrainPopover
                  open={trainPopOpen}
                  popRef={trainPopRef}
                  trainCount={trainCount}
                  maxCount={labeledCount}
                  onToggle={handleOpenTrainPopover}
                  onClose={() => setTrainPopOpen(false)}
                  onCountChange={(v) => {
                    setTrainCount(v);
                  }}
                  onSubmit={handleTrainWithCount}
                />
                {/* <Button onClick={handleAnnotation}>Annotation</Button> */}
                {projectData?.step === "분석" && (
                  <IconButton onClick={handleApplyAILabels}>
                    <Import size="16px" />
                  </IconButton>
                )}
                <Tooltip title={t("JumpToThresholdTooltip")}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<Crosshair size="14px" />}
                      disabled={threshold === undefined || !projectImagesInfo.length}
                      onClick={handleJumpToThreshold}
                      sx={{ fontSize: "12px", minWidth: 0 }}
                    >
                      {threshold !== undefined ? `TH ${threshold}` : "TH -"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={t("TableToolsTooltip")}>
                  <IconButton
                    size="small"
                    onClick={(e) => setToolsAnchor(e.currentTarget)}
                    color={mismatchFilter || selectedSeqs.size ? "primary" : "default"}
                  >
                    <ListFilter size="16px" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  id="auto-train-setting"
                  onClick={() => {
                    setOpenSetting(!openSetting);
                  }}
                >
                  <Settings size="16px" />
                </IconButton>
                {openSetting && (
                  <TrainSettingPanel
                    isSaved={isSaved}
                    autoLabeling={autoLabeling}
                    thresholdCustom={thresholdCustom}
                    targetLabel={targetLabel}
                    minTrainCount={minTrainCount}
                    autoLabelingCondition={autoLabelingCondition}
                    conditionList={conditionList}
                    classList={projectData?.class ?? []}
                    onSave={handleSaveConfig}
                    onAutoLabelingChange={onAutoLabelingChange}
                    onInputChange={handleInputChange}
                    onTargetLabelChange={(v) => {
                      setTargetLabel(v);
                    }}
                    onAutoLabelingConditionChange={(v) => {
                      setAutoLabelingCondition(v);
                    }}
                  />
                )}
              </Box>
            </Box>
            {loadingStatus && (
              <Box
                sx={{
                  position: "fixed",
                  bottom: 24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#1e1e2e",
                  color: "#fff",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: 4,
                  zIndex: 9999,
                }}
              >
                <CircularProgress size="14px" sx={{ color: "#fff" }} />
                <Typography sx={{ fontSize: "13px", color: "#fff" }}>{loadingStatus}</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <TableContainer id="image-list-panel" sx={{ flex: 1 }}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
          }}
          stickyHeader
        >
          <TableHead
            sx={{
              bgcolor: "var(--dg-table-header-bg-color)",
            }}
          >
            <TableRow>
              {fields.map((field, index) => (
                <TableCell
                  key={field}
                  align="center"
                  sx={{
                    color: "var(--dg-table-header-color)",
                    height: "58px",
                    padding: "0 4px",
                    width: `${(getColumnWeight(field) / fields.reduce((s, f) => s + getColumnWeight(f), 0)) * 100}%`,
                  }}
                >
                  {SORTABLE_FIELDS.includes(field) ? (
                    <TableSortLabel
                      active={sort?.key === field}
                      direction={sort?.key === field ? sort.dir : "asc"}
                      onClick={() => handleSort(field as NonNullable<SortState>["key"])}
                    >
                      {field}
                    </TableSortLabel>
                  ) : (
                    field
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
        {isPolling && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 0.5 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              {`${t("ImageLoadingMsg")} ${projectImagesInfo.length} / ${totalFileCount}`}
            </Typography>
          </Box>
        )}
        <VirtualizedSelectableList
          items={viewItems}
          itemHeight={40}
          heightOffset={86}
          selectedItem={selectedRow}
          listRef={listRef}
          onRowClick={handleRowClick}
          dividerIndices={sort || filterSeqs ? undefined : getDividerIndices()} // 구분선은 기본 정렬 기준으로만 의미 있음
          renderRow={(row: IImageInfo, index: number) =>
            fields.map((field, colIndex) => (
              <div
                key={colIndex}
                id={`image-row-${row.id}`}
                onMouseDown={(e) => (clickModRef.current = { shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey })}
                style={{
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: selectedSeqs.has(row.seq!) ? "var(--selected-row-bg)" : undefined,
                  flex: `${getColumnWeight(field)} 1 0`,
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  padding: "0 4px",
                }}
              >
                {renderCellContent(field, row, index)}
              </div>
            ))
          }
        />
      </TableContainer>
      {/* <Popover
        disableRestoreFocus
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              p: 2,
              borderRadius: 2,
              width: 200,
              boxShadow: 8,
            },
          },
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          학습 설정
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label="Epoch"
            type="number"
            size="small"
            fullWidth
            value={epoch}
            onChange={(e) => setEpoch(Number(e.target.value))}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Img per Epoch"
            type="number"
            size="small"
            fullWidth
            value={imgPerEpoch}
            onChange={(e) => setImgPerEpoch(Number(e.target.value))}
            inputProps={{ min: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 0.5,
            }}
          >
            <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} onClick={handleTrainSubmit}>
              시작
            </Button>
          </Box>
        </Box>
      </Popover> */}
    </>
  );
}
